import { useEffect, useMemo, useRef, useState } from 'react'
import type { ChatMessage } from '../../../app/modules/types/chatBot'
import type { StreamingTurn } from '../../../hooks/queries/useChatStream'
import {
  findAssistantAfter,
  hasAssistantAfter,
  isStreamTerminal,
  isTurnPending,
  showStandaloneTyping,
} from './chatTurnState'
import { traceFromTurn, useChatTraceStore } from './chatTraceStore'
import { isEmptyTrace } from './turnActivityState'

export interface UseChatTurnParams {
  chatId: string | null | undefined
  /**
   * Raw query data. `undefined` until the first fetch resolves, and that
   * distinction matters: the composer is live before it, so a turn can start
   * against a list we have not read yet.
   */
  messages: ChatMessage[] | undefined
  streaming: StreamingTurn | null
  streamError: string | null
  /** The send POST is in flight. */
  mutationPending: boolean
}

export interface ChatTurn {
  /**
   * The live turn we are willing to SHOW, which is not the same as the turn the
   * hook is holding — see `staleTurnIdRef` below.
   */
  liveTurn: StreamingTurn | null
  /** Render the live turn's bubble. */
  showLiveTurn: boolean
  /** Render the typing dots INSIDE the live bubble. */
  showLiveTurnDots: boolean
  /** Render the standalone typing dots (no live bubble owns them). */
  showTyping: boolean
  /** Disable the composer. */
  isSending: boolean
  hasError: boolean
  /** Call synchronously when the user sends, BEFORE the mutation fires. */
  beginTurn: () => void
  /** Call with the persisted user message the 202 returned (§8). */
  noteSent: (messageId: string) => void
  /** Call when the send rejects. */
  failTurn: () => void
}

/**
 * Everything about "is a turn outstanding, and what should the user see about
 * it" — extracted from `ChatInner` so it can be driven directly.
 *
 * It was inline, which meant none of it was reachable without a full DOM
 * render: a suite could snapshot a static arrangement but never a TRANSITION,
 * and four separate bugs lived in exactly the transitions nothing exercised.
 * `chatTurnState.ts` holds the pure predicates; this holds the bookkeeping they
 * are fed.
 */
export function useChatTurn({
  chatId,
  messages,
  streaming,
  streamError,
  mutationPending,
}: UseChatTurnParams): ChatTurn {
  const [awaitingResponse, setAwaitingResponse] = useState(false)
  const [sendFailed, setSendFailed] = useState(false)
  /**
   * The id of OUR persisted user message, straight off the 202. State rather
   * than a ref because it is read during render: a ref write would not schedule
   * the re-render that clears the loading cue.
   */
  const [sentMessageId, setSentMessageId] = useState<string | null>(null)

  /**
   * The turn that was in `streaming` when the user last sent.
   *
   * `useChatStream` cannot know a send happened, so it keeps serving the
   * previous exchange's turn (or one replayed out of backfill). Rendering it
   * puts the previous answer directly beneath the new question, where it reads
   * as an instant reply and duplicates its own persisted row. It clears itself:
   * the next turn carries a different `turn_id`.
   */
  const staleTurnIdRef = useRef<string | null>(null)
  /** The message the CURRENT live turn's answer must arrive after. */
  const liveAnchorRef = useRef<string | null>(null)
  /** Which turn `liveAnchorRef` was captured for. */
  const anchorTurnRef = useRef<string | null>(null)
  const prevChatIdRef = useRef<string | null | undefined>(chatId)
  /**
   * The turn whose trace has already been filed, so a re-render cannot rewrite
   * a committed trace with a later, emptier one.
   */
  const committedTurnRef = useRef<string | null>(null)

  const commitTrace = useChatTraceStore((state) => state.commitTrace)

  // Memoised so the anchor effect below does not re-run on every render just
  // because `?? []` allocated a new empty array.
  const list = useMemo(() => messages ?? [], [messages])

  const liveTurn = streaming && streaming.turnId !== staleTurnIdRef.current ? streaming : null
  const streamTerminal = isStreamTerminal(liveTurn)

  /**
   * The live turn has folded into the persisted history, so the bubble can step
   * aside for the real row.
   *
   * Anchored per TURN, not per send. The resumed half of an approval turn is a
   * second assistant message on a new `turn_id`: anchored to the send, the
   * HALTED message would satisfy the resumed turn's test and its answer would
   * blink out the instant it hit `done`, before its own row had landed.
   */
  const turnResolved = streamTerminal && hasAssistantAfter(list, liveAnchorRef.current)

  /**
   * Our own message has been answered. Distinct from `turnResolved`, which is
   * about one streamed turn: this also covers an answer that lands by refetch
   * with no live turn ever observed.
   */
  const assistantLanded = sentMessageId !== null && hasAssistantAfter(list, sentMessageId)

  /**
   * `streamError` is STICKY — `useChatStream` clears it only at the next turn's
   * first event — so believing it verbatim leaves a red banner sitting over a
   * retry that is genuinely in flight, with the typing dots suppressed by the
   * very error the user just acted on. Owing a response is the client's own
   * statement that the last error is no longer the current situation.
   *
   * Safe against an immediate re-error: the hook nulls `error` at the turn
   * boundary before setting the new one, so the `[streamError]` effect below
   * re-fires and drops `awaitingResponse` again.
   */
  const hasError = sendFailed || (!!streamError && !awaitingResponse)

  // Everything belongs to the chat that is going away.
  useEffect(() => {
    if (chatId === prevChatIdRef.current) return
    prevChatIdRef.current = chatId
    setAwaitingResponse(false)
    setSendFailed(false)
    setSentMessageId(null)
    staleTurnIdRef.current = null
    liveAnchorRef.current = null
    anchorTurnRef.current = null
    committedTurnRef.current = null
  }, [chatId])

  /**
   * Anchor each new turn to the last message that existed when it STARTED.
   *
   * The `isStreamTerminal` bail is what keeps a backfill replay from being
   * mistaken for a live turn. Backfill folds its whole history through one
   * batched update, so a turn replayed on mount is already terminal the first
   * time it is ever observed — leaving the anchor at `null` makes
   * `hasAssistantAfter` ask "does this chat already contain an answer", which
   * for a replay is yes, so it reads as resolved on the very first render
   * rather than flashing as live for a frame.
   */
  useEffect(() => {
    if (!liveTurn) return
    if (anchorTurnRef.current === liveTurn.turnId) return
    anchorTurnRef.current = liveTurn.turnId
    if (isStreamTerminal(liveTurn)) return
    liveAnchorRef.current = list[list.length - 1]?.id ?? null
  }, [liveTurn, list])

  useEffect(() => {
    if (assistantLanded) setAwaitingResponse(false)
  }, [assistantLanded])

  /**
   * A terminal turn ends the loading cue even if its persisted row has not
   * landed. On `done` that is a beat earlier than `assistantLanded`; on
   * `approval_required` it is the difference between a usable panel and a
   * composer locked for as long as the user spends reading the card.
   */
  useEffect(() => {
    if (streamTerminal) setAwaitingResponse(false)
  }, [streamTerminal])

  // A stream error ends the turn — stop owing a cue so the user can retry.
  useEffect(() => {
    if (streamError) setAwaitingResponse(false)
  }, [streamError])

  /**
   * File the resolved turn's trace against the message that answered it.
   *
   * This is the only moment the pairing is knowable. Thinking and tool events
   * live only on the stream, and no event carries a `message_id`, so the join
   * has to be made while we still hold both the turn and the anchor it started
   * from — a beat later the live turn is gone and only the persisted row
   * remains.
   *
   * A turn that ends in `error` persists no message, so `findAssistantAfter`
   * finds nothing and this never fires. That is right: `turnResolved` stays
   * false for an errored turn, so its live row stays mounted and its trace stays
   * on screen beneath the banner.
   */
  useEffect(() => {
    if (!chatId || !liveTurn || !streamTerminal) return
    if (committedTurnRef.current === liveTurn.turnId) return

    const answering = findAssistantAfter(list, liveAnchorRef.current)
    if (!answering) return

    committedTurnRef.current = liveTurn.turnId

    const trace = traceFromTurn(liveTurn)
    if (isEmptyTrace(trace)) return

    commitTrace(chatId, answering.id, trace)
  }, [chatId, liveTurn, streamTerminal, list, commitTrace])

  const turnState = {
    mutationPending,
    awaitingResponse,
    streaming: liveTurn,
    turnResolved,
    hasError,
  }

  const showLiveTurn = liveTurn !== null && !turnResolved

  return {
    liveTurn,
    showLiveTurn,
    showLiveTurnDots: showLiveTurn && !streamTerminal && !hasError,
    showTyping: showStandaloneTyping(turnState),
    isSending: isTurnPending(turnState),
    hasError,
    beginTurn: () => {
      setSendFailed(false)
      setAwaitingResponse(true)
      setSentMessageId(null)
      // The RAW turn, not `liveTurn`: whatever the hook is holding right now is
      // what must not be shown as a reply to the message being sent.
      staleTurnIdRef.current = streaming?.turnId ?? null
    },
    noteSent: (messageId: string) => setSentMessageId(messageId),
    failTurn: () => {
      setSendFailed(true)
      setAwaitingResponse(false)
    },
  }
}
